import { Button, Menu, MenuItem, Box, Tooltip, Skeleton, Stack } from "@mui/material";
import { useGetContentItemVersionsQuery, useGetItemPublishingsQuery, useGetLangsQuery } from "../../../../../../../../shell/services/instance";
import { useHistory, useLocation, useParams } from "react-router";
import { KeyboardArrowDownRounded } from "@mui/icons-material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ContentItem } from "../../../../../../../../shell/services/types";
import { AppState } from "../../../../../../../../shell/store/types";
import { selectLang } from "../../../../../../../../shell/store/user";
import { Flag, getCountryCode } from "../../../../../../../../shell/components/Flag";
import { useTranslation } from "react-i18next";
type LanguageSelectorProps = {
  modelZUIDOverride?: string;
  itemZUIDOverride?: string;
  onChange?: (payload: {
    langCode: string;
    siblingZUID?: string;
  }) => void;
  disabled?: boolean;
};
export const LanguageSelector = ({
  modelZUIDOverride,
  itemZUIDOverride,
  onChange,
  disabled = false
}: LanguageSelectorProps) => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {
    modelZUID,
    itemZUID
  } = useParams<{
    modelZUID: string;
    itemZUID: string;
  }>();
  const resolvedModelZUID = modelZUIDOverride || modelZUID;
  const resolvedItemZUID = itemZUIDOverride || itemZUID;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {
    data: _versions
  } = useGetContentItemVersionsQuery({
    modelZUID: resolvedModelZUID,
    itemZUID: resolvedItemZUID
  });
  const {
    data: _itemPublishings
  } = useGetItemPublishingsQuery({
    modelZUID: resolvedModelZUID,
    itemZUID: resolvedItemZUID
  });
  const {
    data: languages,
    isLoading: isLoadingLanguages
  } = useGetLangsQuery({});
  const item = useSelector((state: AppState) => state.content[resolvedItemZUID] as ContentItem);
  const onSelect = (langCode: string) => {
    if (onChange) {
      // @ts-ignore
      const siblingZUID = item?.siblings?.[langCode];
      onChange({
        langCode,
        siblingZUID
      });
      setAnchorEl(null);
      return;
    }
    dispatch(selectLang(langCode));

    // If we are at a content item level then reload newly selected language item
    const parts = location.pathname.split("/");
    // @ts-ignore
    const siblingZUID = item.siblings[langCode];
    if (parts[3] && siblingZUID) {
      const subpath = [...parts];
      // route shape: /content/:model/:item/...
      subpath[2] = resolvedModelZUID;
      subpath[3] = siblingZUID;
      history.push(`${subpath.join("/")}`);
    }
  };
  const activeLanguage = languages?.find(lang => lang.ID === item?.meta?.langID) || {
    code: "en-US"
  };
  if (isLoadingLanguages) {
    return <Stack direction="row" gap={0.5}>
        <Skeleton variant="rounded" width={69} height={20} />
        <KeyboardArrowDownRounded fontSize="small" color="action" />
      </Stack>;
  }
  return <>
      <Tooltip title={t("content.itemEditViewLanguages")} enterDelay={1000} enterNextDelay={1000} placement="top-start" data-sentry-element="Tooltip" data-sentry-source-file="LanguageSelector.tsx">
        <Button sx={{
        color: "text.disabled",
        height: "24px",
        minWidth: "unset",
        padding: "2px",
        " .MuiButton-endIcon": {
          marginLeft: "4px"
        }
      }} color="inherit" endIcon={<KeyboardArrowDownRounded color="action" />} onClick={e => setAnchorEl(e.currentTarget)} data-cy="language-selector" disabled={disabled} data-sentry-element="Button" data-sentry-source-file="LanguageSelector.tsx">
          <Box component="span" color="text.primary" data-sentry-element="Box" data-sentry-source-file="LanguageSelector.tsx">
            <Flag countryCode={getCountryCode(activeLanguage?.code)} data-sentry-element="Flag" data-sentry-source-file="LanguageSelector.tsx" />
          </Box>{" "}
          {activeLanguage?.code?.split("-")[0]?.toUpperCase()} (
          {getCountryCode(activeLanguage?.code)})
        </Button>
      </Tooltip>
      <Menu onClose={() => setAnchorEl(null)} anchorOrigin={{
      vertical: "bottom",
      horizontal: "right"
    }} transformOrigin={{
      vertical: -10,
      horizontal: "right"
    }} anchorEl={anchorEl} open={!!anchorEl} PaperProps={{
      sx: {
        boxShadow: theme => theme.shadows[8],
        width: "280px"
      }
    }} data-sentry-element="Menu" data-sentry-source-file="LanguageSelector.tsx">
        {languages?.map(language => <MenuItem key={language.code} onClick={() => {
        setAnchorEl(null);
        onSelect(language.code);
      }} disabled={disabled}>
            <Flag countryCode={getCountryCode(language.code)} />{" "}
            {language.code.split("-")[0]?.toUpperCase()} (
            {getCountryCode(language.code)})
          </MenuItem>)}
      </Menu>
    </>;
};